import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core';
import { ExpandMore, Print, RemoveCircle, Search, Visibility } from '@material-ui/icons';
import moment from 'moment';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { AppContext } from '../../../providers/app-context-provider/app.context';
import dataProvider from '../../../providers/data-provider';
import { FinishActivityDialog } from './finish-activity-dialog';
import { ReportDialog } from './report-dialog';
import TaskCompletionDialog from './task-completion-dialog';

const LoadingBox = () => (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
    </Box>
);

const formatDate = (string) => {
    const date = moment(string)
    return date.format("DD/MM/YYYY")
}

const getTeamMembersNames = (team, roles) => {
    if (!team?.team_memberships?.length) return '-';
    return team.team_memberships
        .filter(tm => roles.find(role => tm.role.id === role.id))
        .map((m) => m?.user_context?.user?.name)
        .sort()
        .join(' • ');
};

const getRolesNames = (roles) => {
    if (!roles?.length) return '-'
    return roles.map(role => role.name).sort().join(' • ');

}

const getStatusChip = (taskCompletion, is_ai_assisted) => {
    if (!taskCompletion) return null;

    if (taskCompletion.evaluated) {
        return is_ai_assisted ? <Chip label="Corrigido por IA" size='small' color='success' /> : <Chip label="Corrigido" color="primary" size="small" />;
    }

    return is_ai_assisted ? <Chip label="Aguardando correção por IA" color="secondary" size="small" /> : <Chip label="Pendente" color="secondary" size="small" />;
};

const SchoolTeacherDashboard = () => {

    const redirect = useRedirect()
    const notify = useNotify()
    const context = useContext(AppContext)

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [filter, setFilter] = useState({
        has_finished: false,
        pending_only: true,
    });

    const [currentAcademicPeriod, setCurrentAcademicPeriod] = useState(true)
    const [classLoading, setClassLoading] = useState(false)

    const [classes, setClasses] = useState([])
    const [reloadFlag, setReloadFlag] = useState(0);

    const [selected, setSelected] = useState({
        activity: null,
        task: null,
        taskCompletion: null,
    });

    const [reportDialogOpen, setReportDialogOpen] = useState(false)
    const [reportData, setReportData] = useState(null)

    const [finishActivityId, setFinishActivityId] = useState(null)
    const [finishActivityOpen, setFinishActivityOpen] = useState(false)

    const [expandedActivityId, setExpandedActivityId] = useState(null);
    const [expandedTasks, setExpandedTasks] = useState({});

    const handleReportDialogClose = () => { setReportDialogOpen(false) }

    const toggleActivityExpanded = (activityId) => {
        setExpandedActivityId((prev) => (prev === activityId ? null : activityId));
        setExpandedTasks({});
    };

    const toggleTaskExpanded = (activityId, taskId) => {
        setExpandedTasks((prev) => ({
            ...prev,
            [activityId]: prev[activityId] === taskId ? null : taskId,
        }));
    };

    const isTaskExpanded = (activityId, taskId) => {
        return expandedTasks[activityId] === taskId;
    };

    const [uiFilters, setUiFilters] = useState({
        class: '',
        search: '',
        pending_only: true,
        scope: 'active',
    });

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setLoaded(false);

                const resp = await dataProvider.get(
                    'school-activities',
                    'teacher-school-activities',
                    {
                        filter: JSON.stringify(filter),
                        page,
                        limit: perPage,
                        sort: 'school-activities.end_date',
                        order: 'DESC',
                    }
                );
                setData(resp.data.data || []);
                setTotal(resp.data.total || 0);
            } catch (e) {
                console.error('Erro ao carregar atividades do professor: ', e)
                notify('Erro ao carregar atividades do professor.', 'error');
            } finally {
                setLoading(false);
                setLoaded(true);
            }
        })();
    }, [filter, page, perPage, reloadFlag, notify, context]);


    useEffect(() => {
        (async () => {
            try {
                setClassLoading(true)
                await dataProvider.getList('classes', {
                    pagination: { page: 1, perPage: 999 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: currentAcademicPeriod ? { 'academic_period': 'current' } : {}
                })
                    .then(res => {
                        setClasses(res.data)
                        setClassLoading(false)
                    })
            } catch (e) {
                notify('Erro ao carregar turmas', 'error')
                console.error('Erro ao carregar turmas: ', e)
            }
        })()
    }, [notify, currentAcademicPeriod])

    const handleOpenFinishActivityDialog = (id) => { setFinishActivityId(id); setFinishActivityOpen(true) }

    const handleFinishActivityClose = () => { setFinishActivityId(null); setFinishActivityOpen(false) }

    const handleOpenCompletion = (activity, task, taskCompletion) => {
        setSelected({ activity, task, taskCompletion });
    };

    const handleCloseDialog = () => {
        setSelected({ activity: null, task: null, taskCompletion: null });
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        const newPerPage = parseInt(event.target.value, 10);
        setPerPage(newPerPage);
        setPage(1);
    };

    const handleUiFilterChange = (field) => (event) => {
        setUiFilters((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleApplyFilters = () => {
        const newFilter = {};

        if (uiFilters.class) {
            newFilter.class = uiFilters.class;
        }

        if (uiFilters.search) {
            newFilter["school-activities.name"] = uiFilters.search;
        }

        if (uiFilters.pending_only) {
            newFilter.pending_only = uiFilters.pending_only
        }

        if (uiFilters.scope === 'active') {
            newFilter.has_finished = false;
        } else if (uiFilters.scope === 'finished') {
            newFilter.has_finished = true;
        }

        setFilter(newFilter);
        setPage(1);
    };

    const redirectToDashboard = (id) => {
        redirect(`/school-activity-dashboard/${id}`)
    }

    const handleClearFilters = () => {
        setUiFilters({
            class: '',
            search: '',
            scope: 'active',
            pending_only: true,
        });

        const defaultFilter = {
            scope: 'active',
            pending_only: true,
        };

        setFilter(defaultFilter);
        setPage(1);
    };

    const generateGradesReport = (id) => {
        const selected = data.find(el => el.id === id)
        setReportData(selected)
        setReportDialogOpen(true)
    }

    const hasActivities = data && data.length > 0;

    if (loading && !loaded) {
        return <LoadingBox />;
    }

    return (
        <Box p={3}>
            <Typography variant='h4'>{`Dashboard do professor`}</Typography>
            <Divider />
            <Box mb={3}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Typography variant='h6' style={{ marginTop: '16px', marginLeft: '8px' }}>{`Filtros`}</Typography>
                    <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                select
                                fullWidth
                                label="Turma"
                                value={uiFilters.class}
                                onChange={handleUiFilterChange('class')}
                                variant="outlined"
                                margin="dense"
                                disabled={classLoading}
                            >
                                <MenuItem value="">
                                    {`Todas`}
                                </MenuItem>
                                {classes.map((c) =>
                                    <MenuItem value={c.id}>
                                        {c.name}
                                    </MenuItem>
                                )}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4} alignContent='center'>
                            <FormGroup>
                                <FormControlLabel control={<Checkbox />}
                                    label={"Somente período letivo vigente"}
                                    checked={currentAcademicPeriod}
                                    onChange={() => setCurrentAcademicPeriod(v => !v)}
                                    disabled={classLoading}
                                />
                            </FormGroup>
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                select
                                fullWidth
                                label="Situação da atividade"
                                value={uiFilters.scope}
                                onChange={handleUiFilterChange('scope')}
                                variant="outlined"
                                margin="dense"
                            >
                                <MenuItem value="active">
                                    Ativas
                                </MenuItem>
                                <MenuItem value="all">
                                    Ativas e encerradas
                                </MenuItem>
                                <MenuItem value="finished">
                                    Somente encerradas
                                </MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3} alignContent='center'>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={uiFilters.pending_only}
                                            onChange={() =>
                                                setUiFilters(prev => ({
                                                    ...prev,
                                                    pending_only: !prev.pending_only,
                                                }))
                                            }
                                        />
                                    }
                                    label="Apenas pendentes de correção"
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box display="flex" alignItems="center">
                                <TextField
                                    fullWidth
                                    label="Busca por nome"
                                    placeholder="Digite o nome da atividade"
                                    value={uiFilters.search}
                                    onChange={handleUiFilterChange('search')}
                                    variant="outlined"
                                    margin="dense"
                                    InputProps={{ startAdornment: (<InputAdornment position='start'><Search /></InputAdornment>) }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Box display="flex" justifyContent="flex-end" mt={1}>
                            <Button
                                onClick={handleClearFilters}
                                style={{ marginRight: 8 }}
                            >
                                Limpar filtros
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleApplyFilters}
                            >
                                Aplicar filtros
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box style={{ marginBottom: '16px' }}>
                <Typography variant="h6">
                    {!hasActivities ?
                        `Nenhuma atividade ${uiFilters.scope === 'active' ? 'ativa' : uiFilters.scope === 'finished' ? 'encerrada' : ''} \
                    ${uiFilters.pending_only ? 'pendente de correção' : ''} \
                    ${uiFilters.class !== '' ? 'para a turma selecionada' : ''} \
                    ${uiFilters.search ? 'com este nome' : ''} \
                    encontrada para este professor.`
                        :
                        `Exibindo ${total} atividades ${uiFilters.scope === 'active' ? 'ativas' : uiFilters.scope === 'finished' ? 'encerradas' : ''} \
                    ${uiFilters.pending_only ? 'pendentes de correção' : ''} \
                    ${uiFilters.class !== '' ? 'para a turma selecionada' : ''} \
                    ${uiFilters.search ? 'com este nome' : ''}`
                    }
                </Typography>
                <Divider />
            </Box>


            <Grid container spacing={2}>
                {data.map((activity) => (
                    <Grid item xs={12} key={activity.id}>
                        <Card>
                            <CardHeader
                                title={`${activity.name} • ${formatDate(activity.start_date)} a ${formatDate(activity.end_date)}`}
                                subheader={<Chip label={`${activity.class?.name || ''}`} color='primary' size='small' />}
                                action={
                                    <Fragment>
                                        <Button
                                            size='small'
                                            endIcon={<Visibility />}
                                            variant='outlined'
                                            children={`Dashboard`}
                                            onClick={() => redirectToDashboard(activity.id)}
                                            style={{ marginInline: 8 }}
                                        />
                                        <Button
                                            size="small"
                                            endIcon={<Print />}
                                            variant='outlined'
                                            children={`Relatório de notas`}
                                            onClick={() => generateGradesReport(activity.id)}
                                            style={{ marginInline: 8 }}
                                        />
                                        {!activity.has_finished && <Button
                                            size="small"
                                            endIcon={<RemoveCircle />}
                                            variant='outlined'
                                            children={`Encerrar atividade`}
                                            onClick={() => handleOpenFinishActivityDialog(activity.id)}
                                            style={{ marginInline: 8 }}
                                        />}
                                        <Button
                                            onClick={() => toggleActivityExpanded(activity.id)}
                                            aria-expanded={expandedActivityId === activity.id}
                                            aria-label="Mostrar tarefas"
                                            endIcon={<ExpandMore
                                                style={{
                                                    transform: expandedActivityId === activity.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s',
                                                }}
                                            />}
                                            children={expandedActivityId === activity.id ? `Ocultar` : `Exibir`}
                                        />
                                    </Fragment>
                                }
                            />
                            <Collapse in={expandedActivityId === activity.id} timeout="auto" unmountOnExit>
                                <CardContent>
                                    {activity.tasks && activity.tasks.length > 0 ? (
                                        activity.tasks.sort((a, b) => new Date(a.start_at) - new Date(b.start_at)).map((task) => (
                                            <Fragment key={task.id}>
                                                <Box mb={1} mt={2} display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                        <Typography variant="subtitle1">
                                                            {task.name}
                                                        </Typography>
                                                        <Typography variant='body2' color='textSecondary'>

                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {/*`Início: ${formatDate(task.start_at)} • Entrega alunos: ${formatDate(task.student_end_at)} • Prazo correção: ${formatDate(task.teacher_end_at)}`*/}
                                                            {`Prazo correção: ${formatDate(task.teacher_end_at)}`}
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center">
                                                        {task.is_ai_assisted && (
                                                            <Chip
                                                                label={`Atividade assistida por IA`}
                                                                size="small"
                                                                style={{ marginInline: 8, backgroundColor: 'green' }}
                                                            />
                                                        )}
                                                        {task.requires_grade && (
                                                            <Chip
                                                                label={`Valendo ${task.reference_grade || '-'} pts`}
                                                                size="small"
                                                                style={{ marginInline: 8 }}
                                                            />
                                                        )}
                                                        <Button
                                                            size="small"
                                                            onClick={() => toggleTaskExpanded(activity.id, task.id)}
                                                            endIcon={<ExpandMore
                                                                style={{
                                                                    transform: isTaskExpanded(activity.id, task.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                    transition: 'transform 0.2s',
                                                                }}
                                                            />}
                                                            style={{ marginInline: 8 }}
                                                        >
                                                            {isTaskExpanded(activity.id, task.id) ? 'Ocultar entregas' : 'Ver entregas'}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                <Collapse in={isTaskExpanded(activity.id, task.id)} timeout="auto" unmountOnExit>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Equipe</TableCell>
                                                                <TableCell>Membros</TableCell>
                                                                <TableCell>Sujeitos Processuais</TableCell>
                                                                <TableCell>Status</TableCell>
                                                                <TableCell>Nota</TableCell>
                                                                <TableCell align="right">Ações</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {task.task_completions && task.task_completions.length > 0 ? (
                                                                task.task_completions.map((tc) => (
                                                                    <TableRow key={tc.id} hover>
                                                                        <TableCell>
                                                                            {tc.team?.name || '-'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {getTeamMembersNames(tc.team, task.roles)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {getRolesNames(task.roles)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {getStatusChip(tc, task.is_ai_assisted)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {tc.grade != null ? tc.grade : '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <Button
                                                                                size="small"
                                                                                color="primary"
                                                                                variant='contained'
                                                                                onClick={() => handleOpenCompletion(activity, task, tc)}
                                                                                disabled={!tc.evaluated && task.is_ai_assisted}
                                                                            >
                                                                                {tc.evaluated ? 'Editar correção' : task.is_ai_assisted ? 'Aguardando correção por IA' : 'Corrigir'}
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={5}>
                                                                        <Typography variant="body2" color="textSecondary">
                                                                            Nenhuma entrega registrada para esta tarefa.
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </Collapse>
                                            </Fragment>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            Nenhuma tarefa cadastrada para esta atividade.
                                        </Typography>
                                    )}
                                </CardContent>
                            </Collapse>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {hasActivities && (
                <Paper>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <TablePagination
                            component="div"
                            count={total}
                            page={page - 1}
                            onPageChange={handleChangePage}
                            rowsPerPage={perPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            labelRowsPerPage="Itens por página"
                        />
                    </Box>
                </Paper>
            )}

            <TaskCompletionDialog
                open={Boolean(selected.taskCompletion)}
                onClose={handleCloseDialog}
                activity={selected.activity}
                task={selected.task}
                isAiFeedback={selected.task?.is_ai_assisted}
                taskCompletion={selected.taskCompletion}
                onEvaluated={() => setReloadFlag(f => f + 1)}
            />

            <ReportDialog
                open={reportDialogOpen}
                onClose={handleReportDialogClose}
                data={reportData}
            />

            <FinishActivityDialog
                open={finishActivityOpen}
                onClose={handleFinishActivityClose}
                activityId={finishActivityId}
                onFinished={() => setReloadFlag(f => f + 1)}
            />

            {loading && loaded && (
                <Box mt={1}>
                    <Typography variant="caption" color="textSecondary">
                        Atualizando atividades...
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default SchoolTeacherDashboard;
